import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, vModelSelect as _vModelSelect, withDirectives as _withDirectives, vModelText as _vModelText, Fragment as _Fragment, createBlock as _createBlock, withModifiers as _withModifiers, Teleport as _Teleport } from "vue"

const _hoisted_1 = ["title"]
const _hoisted_2 = { key: 0 }
const _hoisted_3 = {
  key: 0,
  class: "storage-alert",
  role: "status"
}
const _hoisted_4 = {
  key: 1,
  class: "storage-alert",
  role: "status"
}
const _hoisted_5 = { class: "storage-heading" }
const _hoisted_6 = { id: "storage-title" }
const _hoisted_7 = ["aria-label", "disabled"]
const _hoisted_8 = { class: "storage-fields" }
const _hoisted_9 = {
  class: "storage-development",
  role: "status"
}
const _hoisted_10 = {
  class: "storage-companion-link",
  href: "https://github.com/xiaomoL444/ugc-tool-desktop/",
  target: "_blank",
  rel: "noopener noreferrer"
}
const _hoisted_11 = ["disabled"]
const _hoisted_12 = { value: "browser" }
const _hoisted_13 = { value: "desktop" }
const _hoisted_14 = ["disabled"]
const _hoisted_15 = ["required", "disabled"]
const _hoisted_16 = ["required", "disabled"]
const _hoisted_17 = ["disabled"]
const _hoisted_18 = {
  key: 0,
  class: "storage-status"
}
const _hoisted_19 = {
  key: 1,
  class: "storage-change"
}
const _hoisted_20 = { class: "storage-note" }
const _hoisted_21 = { class: "storage-actions" }
const _hoisted_22 = ["disabled"]
const _hoisted_23 = ["disabled"]
const _hoisted_24 = ["disabled"]

import { computed, inject, onBeforeUnmount, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import type { StorageClass } from "../services/storage/storage";
import { DesktopStorage, readStorageSettings, saveStorageSettings, type DesktopStatus } from "../services/storage/desktopStorage";
import StorageSyncPanel from './StorageSyncPanel.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'StorageSettings',
  setup(__props) {

const storage = inject<StorageClass>("storage")!;
const desktop = storage.provider instanceof DesktopStorage ? storage.provider : undefined;
const dialog = ref<HTMLDialogElement>();
const draft = reactive(readStorageSettings());
const showSync = ref(false), syncBusy = ref(false), syncLocked = ref(false);
const status = ref<DesktopStatus>({ connected: false, message: "", lastSaveError: "", lastChange: "" });
const unsubscribe = desktop?.onStatus(value => { status.value = value; });
onBeforeUnmount(() => unsubscribe?.());
const busy = ref(false);
const failed = ref(false);
const feedback = ref("");
const origin = window.location.origin;
const { locale, t } = useI18n({ useScope: "global" });
const labels = computed(() => locale.value.startsWith("zh") ? {
  browser: "浏览器存档", disk: "电脑存档", settings: "存储设置", close: "关闭",
  sync: "迁移 / 双向同步存档", synced: "同步操作后已暂停编辑器读写，请刷新页面读取存档。", reload: "刷新并读取存档",
  description: "选择 UGC Tools 的存档位置。电脑模式需要运行本地存档助手。", location: "存档位置",
  companionLink: "本地存档助手（GitHub）",
  step1: "在 C# 程序中选择存档目录。", step2: "将当前网站来源加入程序的“允许的网站”：",
  step3: "启动服务，复制配对码并粘贴到下方。若浏览器询问本地网络访问，请允许。",
  copy: "复制", address: "本地服务地址", token: "配对码", test: "测试连接", connected: "连接成功，可以使用电脑存档。",
  note: "可先通过“迁移 / 双向同步存档”复制已有存档，再切换保存位置。切换设置会刷新页面，请先保存或导出当前编辑。电脑模式断线时不会自动改存到浏览器。",
  apply: "应用设置并刷新", confirm: "即将刷新页面，尚未保存的编辑会丢失。确认已经保存或导出，并应用存储设置？",
  offline: "电脑存档未连接，当前修改尚未确认保存到磁盘。", change: "最近收到的文件变更：", copied: "网站来源已复制。",
} : {
  browser: "Browser saves", disk: "Desktop saves", settings: "Storage settings", close: "Close",
  sync: "Migrate / sync saves", synced: "Editor storage is paused after sync. Reload to read the saves.", reload: "Reload saves",
  description: "Choose where UGC Tools saves your work. Desktop mode requires the local save companion.", location: "Save location",
  companionLink: "Local save companion (GitHub)",
  step1: "Choose a save folder in the C# companion.", step2: "Add this website origin to the companion's allowed websites:",
  step3: "Start the service, copy its pairing code and paste it below. Allow local network access if your browser asks.",
  copy: "Copy", address: "Local service address", token: "Pairing code", test: "Test connection", connected: "Connected. Desktop storage is ready.",
  note: "Use Migrate / sync saves before switching save locations. Applying settings reloads the page; save or export your edits first. Desktop mode never falls back to browser storage on disconnection.",
  apply: "Apply and reload", confirm: "Reloading discards unsaved edits. Have you saved or exported your work and want to apply these settings?",
  offline: "Desktop storage is disconnected. Current edits are not confirmed saved to disk.", change: "Latest file change:", copied: "Website origin copied.",
});

function openSettings() {
  if (!showSync && !syncLocked) Object.assign(draft, readStorageSettings());
  feedback.value = "";
  dialog.value?.showModal();
}
function onCancel(event: Event) { if (syncBusy.value) event.preventDefault(); }
function reloadPage() { window.location.reload(); }

async function check() {
  const probe = new DesktopStorage({ ...draft });
  try { await probe.checkConnection(); }
  finally { probe.dispose(); }
}

async function testConnection() {
  busy.value = true; feedback.value = ""; failed.value = false;
  try { await check(); feedback.value = labels.value.connected; }
  catch (error) { failed.value = true; feedback.value = error instanceof Error ? error.message : String(error); }
  finally { busy.value = false; }
}

async function copyOrigin() {
  try { await navigator.clipboard.writeText(origin); failed.value = false; feedback.value = labels.value.copied; }
  catch { feedback.value = origin; }
}

async function applySettings() {
  if (syncBusy.value || syncLocked.value) return;
  busy.value = true; feedback.value = ""; failed.value = false;
  try {
    if (draft.mode === "desktop") await check();
    if (!window.confirm(labels.value.confirm)) return;
    saveStorageSettings({ ...draft });
    window.location.reload();
  } catch (error) { failed.value = true; feedback.value = error instanceof Error ? error.message : String(error); }
  finally { busy.value = false; }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("button", {
      class: "storage-trigger",
      type: "button",
      title: status.value.message,
      onClick: openSettings
    }, [
      _createElementVNode("span", {
        class: _normalizeClass(["storage-dot", { online: !_unref(desktop) || status.value.connected, failed: !!status.value.lastSaveError }])
      }, null, 2),
      _createTextVNode(" " + _toDisplayString(_unref(desktop) ? labels.value.disk : labels.value.browser) + " ", 1),
      (status.value.lastSaveError)
        ? (_openBlock(), _createElementBlock("span", _hoisted_2, "!"))
        : _createCommentVNode("", true)
    ], 8, _hoisted_1),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (syncLocked.value && !syncBusy.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createElementVNode("span", null, _toDisplayString(labels.value.synced), 1),
            _createElementVNode("button", {
              type: "button",
              onClick: reloadPage
            }, _toDisplayString(labels.value.reload), 1)
          ]))
        : (_unref(desktop) && (status.value.lastSaveError || !status.value.connected))
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createElementVNode("span", null, _toDisplayString(status.value.lastSaveError || labels.value.offline), 1),
              _createElementVNode("button", {
                type: "button",
                onClick: openSettings
              }, _toDisplayString(labels.value.settings), 1)
            ]))
          : _createCommentVNode("", true),
      _createElementVNode("dialog", {
        ref_key: "dialog",
        ref: dialog,
        class: "storage-dialog",
        "aria-labelledby": "storage-title",
        onCancel: onCancel
      }, [
        _createElementVNode("form", {
          onSubmit: _withModifiers(applySettings, ["prevent"])
        }, [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("h2", _hoisted_6, _toDisplayString(labels.value.settings), 1),
            _createElementVNode("button", {
              type: "button",
              "aria-label": labels.value.close,
              disabled: syncBusy.value,
              onClick: _cache[0] || (_cache[0] = ($event: any) => (dialog.value?.close()))
            }, "×", 8, _hoisted_7)
          ]),
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("p", _hoisted_9, _toDisplayString(_unref(t)('common.storageUnderDevelopment')), 1),
            _createElementVNode("p", null, _toDisplayString(labels.value.description), 1),
            _createElementVNode("p", null, [
              _createElementVNode("a", _hoisted_10, _toDisplayString(labels.value.companionLink), 1)
            ]),
            _createElementVNode("label", null, [
              _createTextVNode(_toDisplayString(labels.value.location) + " ", 1),
              _withDirectives(_createElementVNode("select", {
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((draft.mode) = $event)),
                disabled: syncBusy.value || syncLocked.value
              }, [
                _createElementVNode("option", _hoisted_12, _toDisplayString(labels.value.browser), 1),
                _createElementVNode("option", _hoisted_13, _toDisplayString(labels.value.disk), 1)
              ], 8, _hoisted_11), [
                [_vModelSelect, draft.mode]
              ])
            ]),
            _createElementVNode("button", {
              type: "button",
              disabled: syncBusy.value || syncLocked.value,
              onClick: _cache[2] || (_cache[2] = ($event: any) => (showSync.value = !showSync.value))
            }, _toDisplayString(labels.value.sync), 9, _hoisted_14),
            (draft.mode === 'desktop' || showSync.value)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createElementVNode("ol", null, [
                    _createElementVNode("li", null, _toDisplayString(labels.value.step1), 1),
                    _createElementVNode("li", null, [
                      _createTextVNode(_toDisplayString(labels.value.step2) + " ", 1),
                      _createElementVNode("code", null, _toDisplayString(_unref(origin)), 1),
                      _createElementVNode("button", {
                        type: "button",
                        class: "storage-copy",
                        onClick: copyOrigin
                      }, _toDisplayString(labels.value.copy), 1)
                    ]),
                    _createElementVNode("li", null, _toDisplayString(labels.value.step3), 1)
                  ]),
                  _createElementVNode("label", null, [
                    _createTextVNode(_toDisplayString(labels.value.address) + " ", 1),
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((draft.endpoint) = $event)),
                      required: draft.mode === 'desktop',
                      disabled: syncBusy.value || syncLocked.value,
                      spellcheck: "false",
                      placeholder: "http://127.0.0.1:27182"
                    }, null, 8, _hoisted_15), [
                      [_vModelText, draft.endpoint]
                    ])
                  ]),
                  _createElementVNode("label", null, [
                    _createTextVNode(_toDisplayString(labels.value.token) + " ", 1),
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((draft.token) = $event)),
                      type: "password",
                      required: draft.mode === 'desktop',
                      disabled: syncBusy.value || syncLocked.value,
                      autocomplete: "off",
                      spellcheck: "false"
                    }, null, 8, _hoisted_16), [
                      [_vModelText, draft.token]
                    ])
                  ]),
                  _createElementVNode("button", {
                    type: "button",
                    disabled: busy.value || syncBusy.value,
                    onClick: testConnection
                  }, _toDisplayString(labels.value.test), 9, _hoisted_17),
                  (_unref(desktop))
                    ? (_openBlock(), _createElementBlock("p", _hoisted_18, _toDisplayString(status.value.message), 1))
                    : _createCommentVNode("", true),
                  (status.value.lastChange)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_19, _toDisplayString(labels.value.change) + " " + _toDisplayString(status.value.lastChange), 1))
                    : _createCommentVNode("", true)
                ], 64))
              : _createCommentVNode("", true),
            (showSync.value)
              ? (_openBlock(), _createBlock(StorageSyncPanel, {
                  key: 1,
                  settings: draft,
                  onBusy: _cache[5] || (_cache[5] = ($event: any) => (syncBusy.value = $event)),
                  onLocked: _cache[6] || (_cache[6] = ($event: any) => (syncLocked.value = true))
                }, null, 8, ["settings"]))
              : _createCommentVNode("", true),
            _createElementVNode("p", _hoisted_20, _toDisplayString(labels.value.note), 1),
            (feedback.value)
              ? (_openBlock(), _createElementBlock("p", {
                  key: 2,
                  class: _normalizeClass({ 'storage-error': failed.value }),
                  role: "status"
                }, _toDisplayString(feedback.value), 3))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("div", _hoisted_21, [
            _createElementVNode("button", {
              type: "button",
              disabled: syncBusy.value,
              onClick: _cache[7] || (_cache[7] = ($event: any) => (dialog.value?.close()))
            }, _toDisplayString(labels.value.close), 9, _hoisted_22),
            (syncLocked.value)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  class: "storage-primary",
                  type: "button",
                  disabled: syncBusy.value,
                  onClick: reloadPage
                }, _toDisplayString(labels.value.reload), 9, _hoisted_23))
              : (_openBlock(), _createElementBlock("button", {
                  key: 1,
                  class: "storage-primary",
                  type: "submit",
                  disabled: busy.value || syncBusy.value
                }, _toDisplayString(labels.value.apply), 9, _hoisted_24))
          ])
        ], 32)
      ], 544)
    ]))
  ], 64))
}
}

})